import { invalidateReferralConsent } from './consentInvalidation.js';
import supabase from "@/services/supabaseClient";
import { sendProfessionalConnectionEmail } from "@/services/communicationEmailService.js";

const clean = (value) => String(value || "").trim();

export async function loadEligibleReferralSpecialists({ specialty = "", excludeUserId = "" } = {}) {
  const { data, error } = await supabase.rpc("afrimed_list_professional_referral_network", {
    p_specialty: clean(specialty) || null,
  });
  if (error) throw error;

  const excluded = clean(excludeUserId);
  return (data || []).filter((row) => {
    if (clean(row.doctor_user_id) === excluded) return false;
    return row.connection_status === "accepted";
  });
}

export async function loadProfessionalConnections({ specialty = "" } = {}) {
  const { data, error } = await supabase.rpc("afrimed_list_professional_referral_network", {
    p_specialty: clean(specialty) || null,
  });
  if (error) throw error;
  return data || [];
}

export async function loadReferralNetworkDirectory({ search = "", specialty = "", limit = 20, offset = 0 } = {}) {
  const { data, error } = await supabase.rpc("afrimed_list_referral_network_directory", {
    p_search: clean(search) || null,
    p_specialty: clean(specialty) || null,
    p_limit: Math.min(Math.max(Number(limit) || 20, 1), 20),
    p_offset: Math.max(Number(offset) || 0, 0),
  });
  if (error) throw error;
  return data || [];
}

export async function requestProfessionalConnection(recipientDoctorUserId) {
  const { data, error } = await supabase.rpc("afrimed_request_professional_connection", {
    p_recipient_doctor_user_id: recipientDoctorUserId,
  });
  if (error) throw error;
  try {
    await dispatchProfessionalConnectionEmail(data?.connection_id, "requested");
    return { ...data, request_email_dispatched: true };
  } catch (dispatchError) {
    console.warn("Professional connection request email could not be dispatched", dispatchError);
    return { ...data, request_email_dispatched: false };
  }
}

export async function dispatchProfessionalConnectionEmail(connectionId, eventType) {
  return sendProfessionalConnectionEmail({
    connection_id: connectionId,
    event_type: eventType,
  });
}

export async function decideProfessionalConnection(connectionId, decision) {
  const { data, error } = await supabase.rpc("afrimed_decide_professional_connection", {
    p_connection_id: connectionId,
    p_decision: decision,
  });
  if (error) throw error;
  const eventType = data?.status === "accepted" ? "accepted" : "declined";
  void sendProfessionalConnectionEmail({
    connection_id: data?.connection_id,
    event_type: eventType,
  }).catch((dispatchError) => {
    console.warn("Professional connection outcome email could not be dispatched", dispatchError);
  });
  return data;
}

export async function disconnectProfessionalConnection(connectionId) {
  const { data, error } = await supabase.rpc("afrimed_disconnect_professional_connection", {
    p_connection_id: connectionId,
  });
  if (error) throw error;
  return data;
}

export async function createAndPresentReferralPackage({
  clinicalActionId = null,
  referral,
  appointmentId,
  patientUserId,
  referringDoctorUserId,
  specialist,
  clinicalHistory,
  specificRequest,
  urgency,
}) {
  const recipientId = clean(specialist?.doctor_user_id);
  if (!recipientId) throw new Error("Select an eligible specialist.");
  if (import.meta.env.DEV) {
    console.info("[ReferralPackagePreparation]", {
      event: "atomic_rpc_started",
      appointmentId,
      patientUserId,
      referringDoctorUserId,
      receivingDoctorUserId: recipientId,
    });
  }
  const args = {
    p_appointment_id: appointmentId,
    p_patient_user_id: patientUserId,
    p_receiving_doctor_user_id: recipientId,
    p_specialty_label: clean(referral?.to_specialty_label),
    p_clinical_history: clean(clinicalHistory),
    p_specific_request: clean(specificRequest),
    p_urgency: clean(urgency) || "routine",
    p_note_text: clean(specialist?.display_name) || null,
  };
  const { data, error } = clinicalActionId
    ? await supabase.rpc("afrimed_prepare_reviewed_referral", {p_action: clinicalActionId, p_args: args})
    : await supabase.rpc("afrimed_prepare_referral_package", args);
  if (error) {
    if (import.meta.env.DEV) {
      console.info("[ReferralPackagePreparation]", {
        event: "atomic_rpc_failed",
        appointmentId,
        code: error.code || null,
        message: error.message || "unknown_error",
      });
    }
    throw error;
  }
  if (import.meta.env.DEV) {
    console.info("[ReferralPackagePreparation]", {
      event: "atomic_rpc_completed",
      appointmentId,
      referralId: data?.referral_id || null,
      referralPackageId: data?.referral_package_id || null,
      status: data?.status || null,
    });
  }

  return {
    referral: { id: data?.referral_id || "" },
    referralPackage: {
      id: data?.referral_package_id || "",
      status: data?.status || "ready_for_authorisation",
    },
  };
}

export async function decideReferralPackage({
  referralPackage,
  decision,
  createStandingAuthorisation = false,
}) {
  const normalized = decision === "authorised" ? "authorised" : "declined";
  const { data, error } = await supabase.rpc("afrimed_decide_referral_share", {
    p_referral_package_id: referralPackage.id,
    p_decision: normalized,
    p_create_standing_authorisation:
      normalized === "authorised" && Boolean(createStandingAuthorisation),
  });
  if (error) throw error;
  invalidateReferralConsent();
  return data;
}

export async function loadReferralStandingAuthorisations() {
  const { data, error } = await supabase.rpc(
    "afrimed_list_referral_standing_authorisations",
  );
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function revokeReferralStandingAuthorisation(standingAuthorisationId) {
  const { data, error } = await supabase.rpc(
    "afrimed_revoke_referral_standing_authorisation",
    { p_standing_authorisation_id: clean(standingAuthorisationId) },
  );
  if (error) throw error;
  invalidateReferralConsent();
  return data;
}
